/**
 * Who has finished setting up and who has not, and exactly which step each person is stuck on.
 *
 * Progress is read from real data — signed agreement, profile, sessions, availability, bookings
 * on, bank connected for practitioners; storefront, products, ship-from, bank for vendors —
 * never from whether someone has read anything. One list per audience: a maker measured against
 * practitioner steps shows as stuck on an agreement she will never sign.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, RotateCcw, TriangleAlert, Loader2 } from 'lucide-react';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function AdminSetupProgressList({
  endpoint,
  itemsKey,
  title = 'Onboarding progress',
  emptyLabel = 'Nobody here yet.',
  testId = 'admin-progress',
}) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [resetting, setResetting] = useState(null);

  const load = useCallback(() => {
    setFailed(false);
    axios.get(`${API}${endpoint}`, auth())
      .then((r) => setData({ ...r.data, rows: r.data[itemsKey] || [] }))
      .catch(() => {
        setFailed(true);
        setData({ rows: [], total: 0, complete: 0, stalled: 0 });
      });
  }, [endpoint, itemsKey]);

  useEffect(() => { load(); }, [load]);

  const resetWalkthrough = async (row) => {
    setResetting(row.user_id);
    try {
      await axios.post(`${API}/admin/users/${row.user_id}/walkthrough-reset`, {}, auth());
      toast.success(`The walkthrough will open again for ${row.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reset the walkthrough');
    } finally {
      setResetting(null);
    }
  };

  if (!data) {
    return (
      <Card className="bg-white/5 border-white/10">
        <CardContent className="py-10 text-center text-gray-400 text-sm">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/5 border-white/10" data-testid={testId}>
      <CardHeader className="pb-3">
        <CardTitle className="text-white text-base flex flex-wrap items-center gap-3">
          {title}
          <span className="text-xs font-normal text-gray-400">
            {data.complete} of {data.total} finished
          </span>
          {data.stalled > 0 && (
            <span className="text-xs font-normal text-amber-300 flex items-center gap-1.5"
              data-testid={`${testId}-stalled-count`}>
              <TriangleAlert className="w-3.5 h-3.5" /> {data.stalled} stalled
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {failed && (
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3"
            data-testid={`${testId}-error`}>
            <p className="text-sm text-amber-100 flex-1">
              This list could not be loaded. Nobody has been removed — try again.
            </p>
            <Button size="sm" variant="ghost" onClick={load}
              className="h-8 text-xs text-amber-200 hover:text-white hover:bg-amber-500/20"
              data-testid={`${testId}-retry`}>
              Try again
            </Button>
          </div>
        )}

        {!failed && data.rows.length === 0 && (
          <p className="text-sm text-gray-400" data-testid={`${testId}-empty`}>
            {emptyLabel}
          </p>
        )}

        {data.rows.map((row) => (
          <div
            key={row.user_id}
            className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3"
            data-testid={`${testId}-row-${row.user_id}`}
          >
            <div className="min-w-[200px] flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm text-white truncate">{row.full_name || row.email}</p>
                {row.complete
                  ? <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  : row.stalled && <TriangleAlert className="w-3.5 h-3.5 text-amber-300 shrink-0" />}
              </div>
              <p className="text-xs text-gray-400 truncate">{row.email}</p>
            </div>

            <div className="min-w-[180px] flex-1">
              {row.complete ? (
                <p className="text-xs text-emerald-300/80">Everything set up</p>
              ) : (
                <>
                  <p className="text-xs text-gray-300" data-testid={`${testId}-stuck-${row.user_id}`}>
                    Stuck on: {row.stuck_on}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {row.done_count} of {row.required_count} done
                    {row.days_since_claimed !== null && ` · started ${row.days_since_claimed}d ago`}
                  </p>
                </>
              )}
            </div>

            <div className="w-24 shrink-0">
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className={`h-full ${row.complete ? 'bg-emerald-400' : 'bg-amber-400'}`}
                  style={{ width: `${Math.round((row.done_count / Math.max(row.required_count, 1)) * 100)}%` }}
                />
              </div>
            </div>

            <Button
              size="sm"
              variant="ghost"
              onClick={() => resetWalkthrough(row)}
              disabled={resetting === row.user_id}
              className="h-8 text-xs text-gray-300 hover:text-amber-300 hover:bg-amber-500/10"
              title="Make the setup walkthrough open for them again"
              data-testid={`${testId}-reset-${row.user_id}`}
            >
              {resetting === row.user_id
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <RotateCcw className="w-3.5 h-3.5" />}
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
